import { LabelDialogType } from '@tableau/catalog-ui-temp';
import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { defaultInitialState, DialogBodyProps, DialogState, getActionDialogOptions, } from '~src/catalog/components/labels/LabelDialog';
import { getItemsWithCorrectLuids } from '~src/Collections/sagas/CollectionSagas';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../../codegen/Localize';
import { IContentInfo } from '../../../spacesPlatform/models/IContentInfo';
import { SetDataQualityTriggerAction } from '../../actions/labels/SetDataQualityTriggerAction';
import {
  callViaApiService as getDataQualityTriggersForItem,
  DataQualityTriggerInfo,
  GetDataQualityTriggersForItemResponse
} from '../../restApi/dataQuailtyTriggers/getDataQualityTriggersForItem';
import { callViaApiService as setDataQualityTriggersForItem } from '../../restApi/dataQuailtyTriggers/setDataQualityTriggersForItem';

export function* handleSetDataQualityTrigger(action: SetDataQualityTriggerAction): SagaIterator {
  const itemWithCorrectLuid = getItemsWithCorrectLuids([action.item])[0];
  try {

    const existingTriggerResponse: GetDataQualityTriggersForItemResponse = yield call(
      getDataQualityTriggersForItem,
      {
        contentItemType: itemWithCorrectLuid.contentType,
        contentItemLuid: itemWithCorrectLuid.luid,
      }
    );

    yield call(
      openDataQualityTriggerDialog,
      itemWithCorrectLuid,
      existingTriggerResponse.dataQualityTriggerList.dataQualityTrigger[0] ?? undefined,
    );

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [itemWithCorrectLuid]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }

  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}


export function openDataQualityTriggerDialog(item: IContentInfo, existingTrigger: DataQualityTriggerInfo | undefined): PromiseLike<unknown> {

  async function makeApiCallAndDisplayResult(formData: DialogState) {
    const toaster = ToasterService.instance;
    try {
      await setDataQualityTriggersForItem({
        contentItemType: item.contentType,
        contentItemLuid: item.luid,
        dataQualityTrigger: {
          type: getDQType(item.contentType),
          message: formData.message,
          active: formData.isActive,
          severe: formData.isSevere,
        }
      });

      toaster.pop({
        type: ToastType.Info,
        text: getToasterSuccessText(item.contentType, item.name),
      });

    } catch {
      toaster.pop({
        type: ToastType.Error,
        text: getToasterErrorText(item.contentType, item.name),
      });
    }
  }

  const initialBodyProps: DialogBodyProps =
  { contentItem: {contentName: item.name, contentId: item.luid, contentType: getCorrectContentType(item.contentType)} };
  const dialogType = existingTrigger? getLabelTypeFromTriggerType(existingTrigger.type) : getLabelTypeFromContentType(item.contentType);
  const modalInstance = openReactActionDialog(getActionDialogOptions({
    initialBodyProps,
    initialFormData: existingTrigger? getFormDataFromTrigger(existingTrigger) : defaultInitialState(dialogType),
    doAction: makeApiCallAndDisplayResult,
    dialogType,
  }));
  return modalInstance.result;
}

function getFormDataFromTrigger(trigger: DataQualityTriggerInfo): DialogState {
  const dialogType = getLabelTypeFromTriggerType(trigger.type);
  return {
    isActive: trigger.active,
    isSevere: trigger.severe,
    message: trigger.message,
    labelValue: dialogType,
    dialogType,
  };
}

function getLabelTypeFromTriggerType(type: DataQualityTriggerInfo['type']): LabelDialogType {
  switch(type){
    case 'EXTRACT_REFRESH': return 'EXTRACT_REFRESH_FAILURE';
    case 'FLOW_RUN': return 'FLOW_RUN_FAILURE';
  }
}

function getLabelTypeFromContentType(contentType: string): LabelDialogType {
  switch(contentType){
    case 'datasource': return 'EXTRACT_REFRESH_FAILURE';
    case 'flow': return 'FLOW_RUN_FAILURE';
    default: 'warning';
  }
}

function getDQType(contentType: string) {
  if (contentType === 'datasource') {
    return 'extract_refresh';
  } else {
    return 'flow_run';
  }
}

function getToasterErrorText(contentType: string, contentName: string) {
  if (contentType === 'datasource') {
    return Messages.dataQualityTrigger_Error_Datasource({
      CONTENT_NAME: contentName
    });
  } else {
    return Messages.dataQualityTrigger_Error_Flow({
      CONTENT_NAME: contentName
    });
  }
}

function getToasterSuccessText(contentType: string, contentName: string) {
  if (contentType === 'datasource') {
    return Messages.dataQualityTrigger_UpdateSuccess_Datasource({
      CONTENT_NAME: contentName
    });
  } else {
    return Messages.dataQualityTrigger_UpdateSuccess_Flow({
      CONTENT_NAME: contentName
    });
  }
}

function getCorrectContentType(contentType: string) {
  if (contentType === 'datasource') {
    return 'datasource';
  } else {
    return 'flow';
  }
}
